import { Language } from '../../api';
import { buildCsv, coerceNumeric, CSV_BOM, CSV_MIME, parseCsv } from './csv';
import { downloadBlob } from './downloadFile';
import { ExportColumn, ExportFormat, ExportTable } from './exportTypes';

type BaseExportParams = {
  /** Without extension — the util appends .csv or .xlsx. */
  fileName: string;
  format: ExportFormat;
  language: Language;
  sheetName?: string;
};

const deliver = async (table: ExportTable, { fileName, format, language }: BaseExportParams): Promise<void> => {
  if (format === ExportFormat.XLSX) {
    const { buildExcelBlob } = await import('./excel');
    downloadBlob(await buildExcelBlob(table, language), `${fileName}.xlsx`);
    return;
  }
  downloadBlob(new Blob([buildCsv(table)], { type: CSV_MIME }), `${fileName}.csv`);
};

/** For call sites that have typed rows and a column definition. */
export const exportRows = async <TEntity>(
  params: BaseExportParams & {
    columns: ExportColumn<TEntity>[];
    rows: TEntity[];
  },
): Promise<void> => {
  const { columns, rows, sheetName } = params;
  await deliver(
    {
      headers: columns.map((column) => column.header),
      rows: rows.map((row) => columns.map((column) => column.value(row))),
      sheetName,
    },
    params,
  );
};

/**
 * For the two report screens, which have no rows — the server hands them a finished CSV string with
 * headers already localized.
 *
 * The CSV branch passes the server string through untouched apart from the BOM. Re-serializing it
 * through buildCsv would add nothing (Python's csv.writer already emits \r\n and correct quoting)
 * while risking a byte-level change to a feature that works today.
 */
export const exportCsvText = async (params: BaseExportParams & { csv: string }): Promise<void> => {
  const { csv, fileName, format, language, sheetName } = params;

  if (format === ExportFormat.CSV) {
    const withBom = csv.startsWith(CSV_BOM) ? csv : CSV_BOM + csv;
    downloadBlob(new Blob([withBom], { type: CSV_MIME }), `${fileName}.csv`);
    return;
  }

  const [headers = [], ...rows] = parseCsv(csv);
  const { buildExcelBlob } = await import('./excel');
  const blob = await buildExcelBlob({ headers, rows: rows.map((row) => row.map(coerceNumeric)), sheetName }, language);
  downloadBlob(blob, `${fileName}.xlsx`);
};
